(() => {
  const stoneModuleUrl = document.currentScript?.dataset.stoneDrawerScript;
  const MOBILE_QUERY = '(max-width: 989px)';

  const compactShopifyImageUrl = (source, width = 240) => {
    if (!source) return source;
    try {
      const url = new URL(source, window.location.href);
      if (url.hostname === 'cdn.shopify.com' || url.hostname.endsWith('.cdn.shopify.com') ||
          (url.origin === window.location.origin && url.pathname.startsWith('/cdn/shop/'))) {
        url.searchParams.set('width', String(width));
      }
      return url.href;
    } catch (_error) {
      return source;
    }
  };

  const enhanceGallery = (gallery) => {
    if (!gallery || gallery.dataset.lumeruneDotsReady === 'true') return;

    const list = gallery.querySelector('[id^="Slider-Gallery"]');
    const viewer = list?.closest('slider-component');
    const controls = gallery.querySelector('.slider-buttons');
    if (!list || !viewer || !controls) return;

    gallery.dataset.lumeruneDotsReady = 'true';

    const dots = document.createElement('div');
    dots.className = 'lumerune-mobile-dots';
    dots.setAttribute('role', 'tablist');
    dots.setAttribute('aria-label', 'Product images');
    controls.appendChild(dots);

    let slides = [];
    let dotButtons = [];
    let ticking = false;

    const visibleSlides = () => Array.from(list.querySelectorAll('.product__media-item'))
      .filter((slide) => window.getComputedStyle(slide).display !== 'none');

    const setActive = (index) => {
      dotButtons.forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
        dot.setAttribute('tabindex', active ? '0' : '-1');
      });
    };

    const activeFromScroll = () => {
      ticking = false;
      if (!window.matchMedia(MOBILE_QUERY).matches || slides.length < 2) return;

      const viewportCenter = list.scrollLeft + (list.clientWidth / 2);
      let activeIndex = 0;
      let smallestDistance = Number.POSITIVE_INFINITY;

      slides.forEach((slide, index) => {
        const slideCenter = slide.offsetLeft + (slide.offsetWidth / 2);
        const distance = Math.abs(slideCenter - viewportCenter);
        if (distance < smallestDistance) {
          smallestDistance = distance;
          activeIndex = index;
        }
      });

      setActive(activeIndex);
    };

    const rebuild = () => {
      slides = visibleSlides();
      dots.replaceChildren();
      dotButtons = slides.map((slide, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'lumerune-mobile-dot';
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-label', `View product image ${index + 1} of ${slides.length}`);
        button.addEventListener('click', () => {
          const targetLeft = slide.offsetLeft - ((list.clientWidth - slide.offsetWidth) / 2);
          list.scrollTo({ left: Math.max(0, targetLeft), behavior: 'smooth' });
          setActive(index);
        });
        dots.appendChild(button);
        return button;
      });

      dots.hidden = slides.length < 2;
      activeFromScroll();
    };

    list.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(activeFromScroll);
    }, { passive: true });

    viewer.addEventListener('slideChanged', (event) => {
      const index = slides.indexOf(event.detail?.currentElement);
      if (index >= 0) setActive(index);
    });

    let rebuildFrame;
    const observer = new MutationObserver(() => {
      if (rebuildFrame) return;
      rebuildFrame = window.requestAnimationFrame(() => {
        rebuildFrame = null;
        rebuild();
      });
    });
    observer.observe(list, { attributes: true, attributeFilter: ['class', 'style'], childList: true, subtree: true });

    window.addEventListener('resize', activeFromScroll, { passive: true });
    rebuild();
  };

  const enhanceDesktopModal = (modal) => {
    if (!modal || modal.dataset.lumeruneRailReady === 'true') return;

    const hostSection = modal.closest('[data-section-type="main-product"]');
    const dialog = modal.querySelector('.product-media-modal__dialog');
    const content = modal.querySelector('.product-media-modal__content');
    if (!dialog || !content) return;

    const mediaItems = Array.from(content.children)
      .filter((item) => item.hasAttribute('data-media-id') || item.querySelector('[data-media-id]'));
    if (!mediaItems.length) return;

    modal.dataset.lumeruneRailReady = 'true';

    const syncModalLayer = () => {
      hostSection?.classList.toggle('lumerune-media-modal-host-open', modal.hasAttribute('open'));
    };

    const modalLayerObserver = new MutationObserver(syncModalLayer);
    modalLayerObserver.observe(modal, { attributes: true, attributeFilter: ['open'] });
    syncModalLayer();

    const rail = document.createElement('div');
    rail.className = 'lumerune-modal-rail';
    rail.setAttribute('aria-label', 'Product media thumbnails');

    const upButton = document.createElement('button');
    upButton.type = 'button';
    upButton.className = 'lumerune-modal-rail-button lumerune-modal-rail-button--up';
    upButton.setAttribute('aria-label', 'Previous product images');

    const thumbs = document.createElement('div');
    thumbs.className = 'lumerune-modal-thumbs';

    const downButton = document.createElement('button');
    downButton.type = 'button';
    downButton.className = 'lumerune-modal-rail-button lumerune-modal-rail-button--down';
    downButton.setAttribute('aria-label', 'Next product images');

    rail.append(upButton, thumbs, downButton);
    dialog.insertBefore(rail, content);

    // The theme's base media modal closes on any mouse pointer-up outside
    // deferred media. Keep thumbnail interactions inside the open gallery.
    rail.addEventListener('pointerup', (event) => event.stopPropagation());
    rail.addEventListener('click', (event) => event.stopPropagation());
    content.addEventListener('pointerup', (event) => event.stopPropagation());

    let activeIndex = 0;

    const thumbButtons = mediaItems.map((item, index) => {
      const preview = item.matches('img') ? item : item.querySelector('img');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'lumerune-modal-thumb';
      button.setAttribute('aria-label', `View product media ${index + 1} of ${mediaItems.length}`);

      if (!item.matches('img')) button.classList.add('is-video');

      if (preview) {
        const image = document.createElement('img');
        image.src = compactShopifyImageUrl(preview.currentSrc || preview.src);
        image.alt = preview.alt || '';
        image.loading = 'lazy';
        button.appendChild(image);
      }

      button.addEventListener('click', () => activate(index, button));
      thumbs.appendChild(button);
      return button;
    });

    const syncRail = () => {
      const current = mediaItems.findIndex((item) => item.classList.contains('active'));
      if (current >= 0) activeIndex = current;

      thumbButtons.forEach((button, index) => {
        const active = index === activeIndex;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-current', active ? 'true' : 'false');
      });

      if (modal.hasAttribute('open')) {
        thumbButtons[activeIndex]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    };

    const activate = (index, focusTarget = null) => {
      if (index < 0 || index >= mediaItems.length) return;
      window.pauseAllMedia?.();
      mediaItems.forEach((item) => item.classList.remove('active'));
      activeIndex = index;
      const activeItem = mediaItems[activeIndex];
      activeItem.classList.add('active');

      if (activeItem.matches('deferred-media') && typeof activeItem.loadContent === 'function') {
        activeItem.loadContent();
        if (focusTarget) {
          window.requestAnimationFrame(() => focusTarget.focus({ preventScroll: true }));
        }
      }

      syncRail();
    };

    upButton.addEventListener('click', () => thumbs.scrollBy({ top: -260, behavior: 'smooth' }));
    downButton.addEventListener('click', () => thumbs.scrollBy({ top: 260, behavior: 'smooth' }));

    modal.addEventListener('keydown', (event) => {
      if (!window.matchMedia('(min-width: 990px)').matches || !modal.hasAttribute('open')) return;
      if (event.target.closest('button, a, input, select, textarea, video, model-viewer, deferred-media')) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        activate((activeIndex - 1 + mediaItems.length) % mediaItems.length);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        activate((activeIndex + 1) % mediaItems.length);
      }
    });

    const observer = new MutationObserver(syncRail);
    mediaItems.forEach((item) => observer.observe(item, { attributes: true, attributeFilter: ['class'] }));
    syncRail();
  };

  const enhanceDesktopMainSwipe = (gallery) => {
    if (!gallery || gallery.dataset.lumeruneDesktopSwipeReady === 'true') return;

    const viewer = gallery.querySelector('[id^="GalleryViewer"]');
    const list = viewer?.querySelector('[id^="Slider-Gallery"]');
    if (!viewer || !list) return;

    gallery.dataset.lumeruneDesktopSwipeReady = 'true';

    const isDesktop = () => window.matchMedia('(min-width: 990px)').matches;
    const visibleSlides = () => Array.from(list.querySelectorAll('.product__media-item'))
      .filter((slide) => window.getComputedStyle(slide).display !== 'none');

    const setActiveSlide = (slide) => {
      if (!isDesktop() || !slide) return;
      visibleSlides().forEach((item) => item.classList.toggle('is-active', item === slide));
      gallery.playActiveMedia?.(slide);
    };

    viewer.addEventListener('slideChanged', (event) => {
      setActiveSlide(event.detail?.currentElement);
    });

    let pointerId = null;
    let startX = 0;
    let startScrollLeft = 0;
    let dragging = false;
    let suppressClick = false;

    const nearestSlide = () => {
      const slides = visibleSlides();
      if (!slides.length) return null;
      return slides.reduce((nearest, slide) => (
        Math.abs(slide.offsetLeft - list.scrollLeft) < Math.abs(nearest.offsetLeft - list.scrollLeft)
          ? slide
          : nearest
      ), slides[0]);
    };

    const finishPointerDrag = (event) => {
      if (pointerId === null || event.pointerId !== pointerId) return;

      if (dragging) {
        suppressClick = true;
        window.setTimeout(() => { suppressClick = false; }, 400);
        const target = nearestSlide();
        if (target) list.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
      }

      if (list.hasPointerCapture?.(pointerId)) list.releasePointerCapture(pointerId);
      pointerId = null;
      dragging = false;
      list.classList.remove('is-pointer-dragging');
    };

    list.addEventListener('pointerdown', (event) => {
      if (!isDesktop() || event.pointerType !== 'mouse' || event.button !== 0) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startScrollLeft = list.scrollLeft;
      dragging = false;
    });

    list.addEventListener('pointermove', (event) => {
      if (pointerId === null || event.pointerId !== pointerId) return;
      const deltaX = event.clientX - startX;
      if (!dragging && Math.abs(deltaX) < 9) return;
      if (!dragging) {
        dragging = true;
        list.classList.add('is-pointer-dragging');
        list.setPointerCapture?.(pointerId);
      }
      event.preventDefault();
      list.scrollLeft = startScrollLeft - deltaX;
    }, { passive: false });

    list.addEventListener('pointerup', finishPointerDrag);
    list.addEventListener('pointercancel', finishPointerDrag);
    list.addEventListener('dragstart', (event) => event.preventDefault());

    list.addEventListener('click', (event) => {
      if (!suppressClick) return;
      suppressClick = false;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }, true);

    window.addEventListener('blur', () => {
      pointerId = null;
      dragging = false;
      list.classList.remove('is-pointer-dragging');
    });
  };

  const enhanceDesktopInlineGallery = (gallery) => {
    if (!gallery || gallery.dataset.lumeruneVerticalRailReady === 'true') return;

    const rail = gallery.querySelector('.thumbnail-slider');
    const list = rail?.querySelector('.thumbnail-list');
    const previousButton = rail?.querySelector('.slider-button--prev');
    const nextButton = rail?.querySelector('.slider-button--next');
    if (!rail || !list || !previousButton || !nextButton) return;

    gallery.dataset.lumeruneVerticalRailReady = 'true';

    const isDesktop = () => window.matchMedia('(min-width: 990px)').matches;
    const thumbnailButtons = () => Array.from(list.querySelectorAll('.thumbnail'))
      .filter((button) => window.getComputedStyle(button.closest('.thumbnail-list__item')).display !== 'none');

    const updateButtons = () => {
      const desktop = isDesktop();

      // The theme's slider component measures this rail horizontally. Once
      // the thumbnails become a vertical desktop rail, that calculation can
      // incorrectly disable the down button after resize/image loading.
      // Use the component's looping escape hatch while our vertical controls
      // are active so its ResizeObserver no longer owns the disabled state.
      rail.enableSliderLooping = desktop;

      if (!desktop) {
        previousButton.disabled = false;
        nextButton.disabled = false;
        previousButton.classList.remove('is-disabled');
        nextButton.classList.remove('is-disabled');
        previousButton.removeAttribute('aria-disabled');
        nextButton.removeAttribute('aria-disabled');
        return;
      }

      previousButton.disabled = true;
      previousButton.classList.add('is-disabled');
      previousButton.setAttribute('aria-disabled', 'true');

      nextButton.disabled = false;
      nextButton.classList.remove('is-disabled');
      nextButton.setAttribute('aria-disabled', 'false');
    };

    const scrollPage = (direction) => {
      if (!isDesktop()) return;
      const items = thumbnailButtons();
      const firstItem = items[0]?.closest('.thumbnail-list__item');
      const styles = window.getComputedStyle(list);
      const gap = Number.parseFloat(styles.rowGap || styles.gap) || 0;
      const itemHeight = firstItem?.getBoundingClientRect().height || 0;
      const visibleCount = itemHeight > 0
        ? Math.max(1, Math.round((list.clientHeight + gap) / (itemHeight + gap)))
        : 1;
      const step = itemHeight > 0 ? (itemHeight + gap) * visibleCount : list.clientHeight;
      const maxScroll = Math.max(0, list.scrollHeight - list.clientHeight);

      if (direction > 0 && maxScroll > 2 && list.scrollTop >= maxScroll - 2) {
        list.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      list.scrollBy({ top: direction * step, behavior: 'smooth' });
    };

    const interceptRailButton = (event, direction) => {
      if (!isDesktop()) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      scrollPage(direction);
    };

    previousButton.addEventListener('click', (event) => interceptRailButton(event, -1), true);
    nextButton.addEventListener('click', (event) => interceptRailButton(event, 1), true);
    list.addEventListener('scroll', updateButtons, { passive: true });

    // Guard against any already-queued legacy slider update that was created
    // before enableSliderLooping was enabled. Attribute mutations flush before
    // the next paint, preventing the visible-but-unclickable first-load state.
    const nextStateObserver = new MutationObserver(() => {
      if (isDesktop() && nextButton.disabled) updateButtons();
    });
    nextStateObserver.observe(nextButton, {
      attributes: true,
      attributeFilter: ['disabled'],
    });

    list.addEventListener('click', (event) => {
      const button = event.target.closest('.thumbnail');
      if (!button) return;
      window.requestAnimationFrame(() => {
        button.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        updateButtons();
      });
    });

    const currentObserver = new MutationObserver(() => {
      const active = list.querySelector('.thumbnail[aria-current]');
      if (active && isDesktop()) active.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      updateButtons();
    });

    thumbnailButtons().forEach((button) => {
      currentObserver.observe(button, { attributes: true, attributeFilter: ['aria-current'] });
    });

    window.addEventListener('resize', updateButtons, { passive: true });
    updateButtons();
  };

  const enhanceProductIntro = (wrapper) => {
    if (!wrapper || wrapper.dataset.lumeruneIntroReady === 'true') return;

    const intro = wrapper.querySelector('.lumerune-product-intro');
    const toggle = wrapper.querySelector('[data-lumerune-product-intro-toggle]');
    if (!intro || !toggle) return;

    wrapper.dataset.lumeruneIntroReady = 'true';
    const moreLabel = toggle.querySelector('[data-lumerune-intro-more]');
    const lessLabel = toggle.querySelector('[data-lumerune-intro-less]');

    const setExpanded = (expanded) => {
      toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      intro.classList.toggle('is-collapsed', !expanded);
      wrapper.classList.toggle('is-expanded', expanded);
      if (moreLabel) moreLabel.hidden = expanded;
      if (lessLabel) lessLabel.hidden = !expanded;
    };

    const hasCollapsedOverflow = () => {
      const wasCollapsed = intro.classList.contains('is-collapsed');
      intro.classList.add('is-collapsed');
      const hasOverflow = intro.scrollHeight > intro.clientHeight + 1;
      if (!wasCollapsed) intro.classList.remove('is-collapsed');
      return hasOverflow;
    };

    const syncToggle = () => {
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      const hasOverflow = hasCollapsedOverflow();
      toggle.hidden = !hasOverflow;

      if (!hasOverflow) {
        if (expanded) setExpanded(false);
        intro.classList.remove('is-collapsed');
        return;
      }

      if (!expanded) intro.classList.add('is-collapsed');
    };

    toggle.addEventListener('click', () => {
      setExpanded(toggle.getAttribute('aria-expanded') !== 'true');
    });

    if ('ResizeObserver' in window) {
      let observedWidth = -1;
      const resizeObserver = new ResizeObserver((entries) => {
        const nextWidth = Math.round(entries[0].contentRect.width);
        if (nextWidth === observedWidth) return;
        observedWidth = nextWidth;
        syncToggle();
      });
      resizeObserver.observe(wrapper);
    } else {
      window.addEventListener('resize', syncToggle, { passive: true });
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(syncToggle);
    }

    syncToggle();
  };

  let stoneModulePromise;
  const pendingDrawers = new WeakMap();

  const prepareStoneDrawer = (drawer) => {
    if (drawer.dataset.lumeruneStoneReady === 'true') return Promise.resolve();
    if (pendingDrawers.has(drawer)) return pendingDrawers.get(drawer);
    if (!stoneModulePromise) {
      stoneModulePromise = import(stoneModuleUrl).catch((error) => {
        stoneModulePromise = null;
        throw error;
      });
    }
    const pending = stoneModulePromise.then((module) => {
      if (drawer.isConnected) module.enhanceStoneDrawer(drawer);
    }).finally(() => pendingDrawers.delete(drawer));
    pendingDrawers.set(drawer, pending);
    return pending;
  };

  const deferStoneDrawer = (drawer) => {
    if (drawer.dataset.lumeruneStoneDeferred === 'true') return;
    const section = drawer.closest('[data-section-type="main-product"]');
    const trigger = section?.querySelector('[data-lumerune-stone-drawer-open]');
    if (!trigger || !stoneModuleUrl) return;
    drawer.dataset.lumeruneStoneDeferred = 'true';
    let opening = false;
    const prepare = () => prepareStoneDrawer(drawer).catch((error) => {
      console.error('LUMERUNE gemstone drawer:', error);
    });
    trigger.addEventListener('pointerenter', prepare, { once: true });
    trigger.addEventListener('focus', prepare, { once: true });
    trigger.addEventListener('click', (event) => {
      if (drawer.dataset.lumeruneStoneReady === 'true') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (opening) return;
      opening = true;
      trigger.setAttribute('aria-busy', 'true');
      prepareStoneDrawer(drawer).then(() => {
        if (trigger.isConnected && drawer.dataset.lumeruneStoneReady === 'true') trigger.click();
      }).catch((error) => {
        console.error('LUMERUNE gemstone drawer:', error);
      }).finally(() => {
        opening = false;
        trigger.removeAttribute('aria-busy');
      });
    }, true);

    let scheduled = false;
    const afterImage = () => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
        if ('requestIdleCallback' in window) window.requestIdleCallback(prepare, { timeout: 2000 });
        else window.setTimeout(prepare, 0);
      }));
    };
    const hero = section.querySelector('media-gallery img[fetchpriority="high"]');
    if (!hero || hero.complete) afterImage();
    else {
      hero.addEventListener('load', afterImage, { once: true });
      hero.addEventListener('error', afterImage, { once: true });
    }
  };

  const deferDesktopModal = (modal) => {
    if (modal.dataset.lumeruneModalDeferred === 'true') return;
    modal.dataset.lumeruneModalDeferred = 'true';
    if (modal.hasAttribute('open')) {
      enhanceDesktopModal(modal);
      return;
    }
    const observer = new MutationObserver(() => {
      if (!modal.hasAttribute('open')) return;
      observer.disconnect();
      enhanceDesktopModal(modal);
    });
    observer.observe(modal, { attributes: true, attributeFilter: ['open'] });
  };

  const init = (root = document) => {
    root.querySelectorAll('media-gallery').forEach((gallery) => {
      enhanceGallery(gallery);
      enhanceDesktopMainSwipe(gallery);
      enhanceDesktopInlineGallery(gallery);
    });
    root.querySelectorAll('product-modal').forEach(deferDesktopModal);
    root.querySelectorAll('[data-lumerune-product-intro]').forEach(enhanceProductIntro);
    root.querySelectorAll('[data-lumerune-stone-drawer]').forEach(deferStoneDrawer);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();

